import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import PublicHeader from './PublicHeader';
import PublicFooter from './PublicFooter';
import ScrollToTop from './ScrollToTop';
import SEOHead from './SEOHead';

const PrivacyPage = () => {
  const [currency, setCurrency] = useState('USD');

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="Privacy Policy - Global Trade Car"
        description="Read our privacy policy to understand how we collect, use, and protect your personal information."
      />
      <PublicHeader currency={currency} onCurrencyChange={setCurrency} />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-6">Privacy Policy</h1>
        <p className="text-gray-600 mb-8">Last updated: December 15, 2024</p>

        <Card className="mb-6">
          <CardContent className="p-8 prose prose-red max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-600 mb-4">
              Global Trade Car ("we", "our", or "us") is committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
              when you visit our website or use our services.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">2. Information We Collect</h2>
            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">2.1 Personal Information</h3>
            <p className="text-gray-600 mb-4">
              We collect information that you voluntarily provide to us when you:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4">
              <li>Submit an inquiry or contact form</li>
              <li>Request a quote or vehicle information</li>
              <li>Make a purchase</li>
              <li>Subscribe to our newsletter</li>
              <li>Communicate with us via email, phone, or chat</li>
            </ul>
            <p className="text-gray-600 mb-4">
              This information may include: name, email address, phone number, country, shipping address, 
              and payment information.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">2.2 Automatically Collected Information</h3>
            <p className="text-gray-600 mb-4">
              When you visit our website, we may automatically collect certain information about your 
              device and browsing actions, including:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4">
              <li>IP address and location data</li>
              <li>Browser type and version</li>
              <li>Pages visited and time spent</li>
              <li>Referring website</li>
              <li>Device information</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">3. How We Use Your Information</h2>
            <p className="text-gray-600 mb-4">
              We use the collected information for the following purposes:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4">
              <li>Process and fulfill vehicle orders and inquiries</li>
              <li>Communicate with you about your purchase or inquiry</li>
              <li>Provide customer support and respond to your questions</li>
              <li>Send you updates about vehicles, services, and promotions</li>
              <li>Improve our website and services</li>
              <li>Prevent fraud and ensure security</li>
              <li>Comply with legal obligations</li>
              <li>Analyze website usage and trends</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">4. Information Sharing and Disclosure</h2>
            <p className="text-gray-600 mb-4">
              We do not sell, trade, or rent your personal information to third parties. We may share 
              your information only in the following circumstances:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4">
              <li><strong>Service Providers:</strong> With shipping companies, payment processors, and 
              other service providers necessary to fulfill your order</li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, or government request</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale 
              of company assets</li>
              <li><strong>With Your Consent:</strong> When you explicitly authorize us to share your information</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">5. Cookies and Tracking Technologies</h2>
            <p className="text-gray-600 mb-4">
              We use cookies and similar tracking technologies to improve your browsing experience. 
              Cookies are small data files stored on your device. You can control cookie settings through 
              your browser, but disabling cookies may affect website functionality.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">6. Data Security</h2>
            <p className="text-gray-600 mb-4">
              We implement appropriate technical and organizational security measures to protect your 
              personal information against unauthorized access, alteration, disclosure, or destruction. 
              However, no internet transmission is 100% secure, and we cannot guarantee absolute security.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">7. Data Retention</h2>
            <p className="text-gray-600 mb-4">
              We retain your personal information for as long as necessary to fulfill the purposes 
              outlined in this policy, unless a longer retention period is required by law. After this 
              period, we will securely delete or anonymize your information.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">8. Your Privacy Rights</h2>
            <p className="text-gray-600 mb-4">
              Depending on your location, you may have the following rights regarding your personal information:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4">
              <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Objection:</strong> Object to processing of your personal information</li>
              <li><strong>Portability:</strong> Request transfer of your data to another service</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent for data processing at any time</li>
            </ul>
            <p className="text-gray-600 mb-4">
              To exercise these rights, please contact us at sales@globaltradecar.com.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">9. Third-Party Websites</h2>
            <p className="text-gray-600 mb-4">
              Our website may contain links to third-party websites. We are not responsible for the 
              privacy practices of these websites. We encourage you to read their privacy policies.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">10. Children's Privacy</h2>
            <p className="text-gray-600 mb-4">
              Our services are not directed to individuals under 18 years of age. We do not knowingly 
              collect personal information from children. If you believe we have collected information 
              from a child, please contact us immediately.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">11. International Data Transfers</h2>
            <p className="text-gray-600 mb-4">
              Your information may be transferred to and processed in countries other than your country 
              of residence. We ensure appropriate safeguards are in place to protect your information 
              in accordance with this privacy policy.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">12. Changes to This Privacy Policy</h2>
            <p className="text-gray-600 mb-4">
              We may update this privacy policy from time to time. We will notify you of any changes by 
              posting the new policy on this page with an updated revision date. We encourage you to 
              review this policy periodically.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">13. Contact Us</h2>
            <p className="text-gray-600 mb-4">
              If you have questions or concerns about this privacy policy or our data practices, 
              please contact us:
            </p>
            <ul className="list-none text-gray-600 mb-4">
              <li><strong>Email:</strong> sales@globaltradecar.com</li>
              <li><strong>Phone:</strong> +81 70-9270-2242</li>
              <li><strong>Address:</strong> [Your Business Address]</li>
            </ul>
          </CardContent>
        </Card>
      </div>

      <PublicFooter />
      <ScrollToTop />
    </div>
  );
};

export default PrivacyPage;